import type { AuthUser } from "@apple-bee/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Archive,
  CheckCircle2,
  Clock3,
  Database,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";
import { api, errorMessage } from "../api";
import { EmptyState, LoadingBlock, Message } from "../components/Feedback";
import { Modal } from "../components/Modal";
import { dateTimeFormatter } from "../format";

type VerificationStatus = "UNVERIFIED" | "APPROVED" | "REJECTED";
type StatusFilter = VerificationStatus | "ALL";

type LegacyReferenceRecord = {
  id: string;
  dataset: string;
  sourceKey: string;
  record: Record<string, unknown>;
  verificationStatus: VerificationStatus;
  reviewedBy: string | null;
  reviewerName: string | null;
  reviewedAt: string | null;
  reviewNote: string | null;
  createdAt: string;
};

type LegacyReferenceResponse = {
  records: LegacyReferenceRecord[];
  summary: {
    total: number;
    unverified: number;
    approved: number;
    rejected: number;
    datasets: Array<{
      dataset: string;
      total: number;
      unverified: number;
      approved: number;
      rejected: number;
    }>;
  };
};

type Decision = {
  record: LegacyReferenceRecord;
  status: "APPROVED" | "REJECTED";
};

const datasetLabels: Record<string, string> = {
  blocks: "Садовые блоки",
  boxtypes: "Типы упаковки",
  carriers: "Перевозчики",
  contractors: "Подрядчики",
  destinations: "Направления",
  drivers: "Водители",
  farmRoutes: "Маршруты хозяйства",
  gardens: "Сады",
  plots: "Участки",
  rentRates: "Арендные ставки",
};

const statusLabels: Record<VerificationStatus, string> = {
  UNVERIFIED: "Не проверено",
  APPROVED: "Одобрено",
  REJECTED: "Отклонено",
};

function displayValue(value: unknown): string {
  if (value === null || value === undefined || value === "") return "—";
  if (typeof value === "boolean") return value ? "Да" : "Нет";
  if (Array.isArray(value)) return value.map(displayValue).join(", ") || "—";
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

function recordTitle(record: LegacyReferenceRecord): string {
  for (const key of ["name", "desc", "city", "to", "code"]) {
    const value = record.record[key];
    if (typeof value === "string" && value.trim()) return value;
  }
  return record.sourceKey;
}

function recordDetails(record: LegacyReferenceRecord) {
  return Object.entries(record.record)
    .filter(([key]) => !["id", "name", "desc", "city", "to"].includes(key))
    .slice(0, 4);
}

export function PrototypeDataPage({ user }: { user: AuthUser }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [dataset, setDataset] = useState("ALL");
  const [status, setStatus] = useState<StatusFilter>("ALL");
  const [decision, setDecision] = useState<Decision | null>(null);
  const [reviewNote, setReviewNote] = useState("");
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const canReview = user.roles.includes("CEO");

  const query = useQuery({
    queryKey: ["legacy-references"],
    queryFn: () => api<LegacyReferenceResponse>("/api/legacy-references/"),
  });

  const mutation = useMutation({
    mutationFn: (input: { id: string; status: "APPROVED" | "REJECTED"; note: string }) =>
      api(`/api/legacy-references/${input.id}/review`, {
        method: "POST",
        body: JSON.stringify({
          status: input.status,
          note: input.note.trim() || undefined,
        }),
      }),
    onSuccess: async (_response, input) => {
      await queryClient.invalidateQueries({ queryKey: ["legacy-references"] });
      setSuccessMessage(
        input.status === "APPROVED"
          ? "Запись одобрена для последующей нормализации."
          : "Запись отклонена и останется вне рабочих процессов.",
      );
      setDecision(null);
      setReviewNote("");
    },
  });

  const filteredRecords = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase("ru-RU");
    return (query.data?.records ?? []).filter((record) => {
      if (dataset !== "ALL" && record.dataset !== dataset) return false;
      if (status !== "ALL" && record.verificationStatus !== status) return false;
      if (!normalizedSearch) return true;
      return `${record.sourceKey} ${JSON.stringify(record.record)}`
        .toLocaleLowerCase("ru-RU")
        .includes(normalizedSearch);
    });
  }, [dataset, query.data?.records, search, status]);

  function openDecision(
    record: LegacyReferenceRecord,
    nextStatus: "APPROVED" | "REJECTED",
  ) {
    setSuccessMessage(null);
    mutation.reset();
    setReviewNote("");
    setDecision({ record, status: nextStatus });
  }

  function submitDecision(event: FormEvent) {
    event.preventDefault();
    if (!decision) return;
    mutation.mutate({
      id: decision.record.id,
      status: decision.status,
      note: reviewNote,
    });
  }

  if (query.isLoading) {
    return <LoadingBlock label="Загружаем данные прототипа…" />;
  }

  return (
    <div className="page-stack">
      <section className="page-heading">
        <div>
          <p className="eyebrow">Контур проверки</p>
          <h2>Данные прототипа</h2>
          <p>
            Эти записи перенесены из HTML-прототипа и не участвуют в остатках,
            заявках или бухгалтерских операциях до отдельной нормализации.
          </p>
        </div>
        <span className="audit-seal">
          <ShieldCheck size={20} />
          {canReview ? "Решение принимает CEO" : "Только просмотр"}
        </span>
      </section>

      <section className="reference-summary">
        <article>
          <Database size={20} />
          <span><strong>{query.data?.summary.total ?? 0}</strong> всего записей</span>
        </article>
        <article className="reference-summary--unverified">
          <Clock3 size={20} />
          <span><strong>{query.data?.summary.unverified ?? 0}</strong> не проверено</span>
        </article>
        <article className="reference-summary--approved">
          <CheckCircle2 size={20} />
          <span><strong>{query.data?.summary.approved ?? 0}</strong> одобрено</span>
        </article>
        <article className="reference-summary--rejected">
          <XCircle size={20} />
          <span><strong>{query.data?.summary.rejected ?? 0}</strong> отклонено</span>
        </article>
      </section>

      <div className="prototype-notice">
        <Archive size={20} />
        <p>
          Одобрение подтверждает только достоверность записи. Оно не добавляет
          запись в рабочий справочник автоматически.
        </p>
      </div>

      {query.error ? <Message tone="error">{errorMessage(query.error)}</Message> : null}
      {mutation.error ? <Message tone="error">{errorMessage(mutation.error)}</Message> : null}
      {successMessage ? <Message tone="success">{successMessage}</Message> : null}

      <section className="panel">
        <div className="prototype-filters">
          <label>
            Поиск
            <input
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Название, код или поле записи"
            />
          </label>
          <label>
            Набор данных
            <select value={dataset} onChange={(event) => setDataset(event.target.value)}>
              <option value="ALL">Все наборы</option>
              {(query.data?.summary.datasets ?? []).map((item) => (
                <option key={item.dataset} value={item.dataset}>
                  {datasetLabels[item.dataset] ?? item.dataset} ({item.total})
                </option>
              ))}
            </select>
          </label>
          <label>
            Статус
            <select
              value={status}
              onChange={(event) => setStatus(event.target.value as StatusFilter)}
            >
              <option value="ALL">Все статусы</option>
              <option value="UNVERIFIED">Не проверено</option>
              <option value="APPROVED">Одобрено</option>
              <option value="REJECTED">Отклонено</option>
            </select>
          </label>
          <span className="table-toolbar__count">{filteredRecords.length} записей</span>
        </div>

        {filteredRecords.length ? (
          <div className="table-wrap">
            <table className="prototype-table">
              <thead>
                <tr>
                  <th>Набор</th>
                  <th>Запись</th>
                  <th>Поля прототипа</th>
                  <th>Статус</th>
                  <th>Проверка</th>
                  <th aria-label="Действия" />
                </tr>
              </thead>
              <tbody>
                {filteredRecords.map((record) => (
                  <tr key={record.id}>
                    <td>
                      <strong>{datasetLabels[record.dataset] ?? record.dataset}</strong>
                      <span className="table-subline">{record.dataset}</span>
                    </td>
                    <td>
                      <strong>{recordTitle(record)}</strong>
                      <span className="table-subline">{record.sourceKey}</span>
                    </td>
                    <td>
                      <div className="reference-details">
                        {recordDetails(record).map(([key, value]) => (
                          <span key={key} title={displayValue(value)}>
                            <b>{key}</b>: {displayValue(value)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>
                      <span
                        className={`status-pill status-pill--${record.verificationStatus.toLowerCase()}`}
                      >
                        {statusLabels[record.verificationStatus]}
                      </span>
                    </td>
                    <td>
                      {record.reviewedAt ? (
                        <>
                          <strong>{record.reviewerName ?? "CEO"}</strong>
                          <span className="table-subline">
                            {dateTimeFormatter.format(new Date(record.reviewedAt))}
                          </span>
                          {record.reviewNote ? (
                            <span className="table-subline" title={record.reviewNote}>
                              {record.reviewNote}
                            </span>
                          ) : null}
                        </>
                      ) : (
                        <span className="muted">Ожидает решения</span>
                      )}
                    </td>
                    <td>
                      {canReview ? (
                        <div className="review-actions">
                          <button
                            className="button button--secondary button--small"
                            type="button"
                            disabled={record.verificationStatus === "APPROVED"}
                            onClick={() => openDecision(record, "APPROVED")}
                          >
                            Одобрить
                          </button>
                          <button
                            className="button button--danger-ghost button--small"
                            type="button"
                            disabled={record.verificationStatus === "REJECTED"}
                            onClick={() => openDecision(record, "REJECTED")}
                          >
                            Отклонить
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <EmptyState
            title="Записи не найдены"
            description="Измените поиск, набор данных или фильтр статуса."
          />
        )}
      </section>

      {decision ? (
        <Modal
          title={decision.status === "APPROVED" ? "Одобрить запись" : "Отклонить запись"}
          subtitle={`${datasetLabels[decision.record.dataset] ?? decision.record.dataset} · ${recordTitle(decision.record)}`}
          onClose={() => {
            if (!mutation.isPending) setDecision(null);
          }}
        >
          <form className="modal__body form-stack" onSubmit={submitDecision}>
            <p className="review-explanation">
              {decision.status === "APPROVED"
                ? "Запись будет отмечена как проверенная, но останется вне рабочих процессов до нормализации."
                : "Запись останется в истории импорта, но не будет использоваться при нормализации."}
            </p>
            <label>
              Комментарий CEO
              <textarea
                value={reviewNote}
                maxLength={500}
                onChange={(event) => setReviewNote(event.target.value)}
                placeholder="Причина решения или источник проверки"
              />
              <span className="field-hint">До 500 символов · необязательно</span>
            </label>
            <div className="modal__actions">
              <button
                className="button button--secondary"
                type="button"
                disabled={mutation.isPending}
                onClick={() => setDecision(null)}
              >
                Отмена
              </button>
              <button
                className={
                  decision.status === "APPROVED"
                    ? "button button--primary"
                    : "button button--danger-ghost"
                }
                type="submit"
                disabled={mutation.isPending}
              >
                {mutation.isPending ? "Сохраняем…" : "Подтвердить решение"}
              </button>
            </div>
          </form>
        </Modal>
      ) : null}
    </div>
  );
}
